<template>
	<section class="no-marginbottom">
		<el-row>
			<el-col :sm="12" :xs="24">
				<h2>Medien</h2>
			</el-col>
			<el-col :sm="12" :xs="24" class="flex flex--justifyend" v-if="showAddButton">
				<TheButton @buttonclick="openTheDialog">
					<el-icon><Plus /></el-icon>Datei hinzufügen
				</TheButton>
			</el-col>
		</el-row>
	</section>

	<MediaSlider v-if="parsedFiles.length > 0" :files="parsedFiles" :allowDeleteFromServer="allowDeleteFromServer" @removeFromFiles="removeFromFiles" />
	<!-- needed for form validation -->
	<el-form-item label="" prop="files" class="is-hidden">
		<el-input v-model="parsedFiles" />
	</el-form-item>

	<el-dialog v-model="openDialog" :title="'Dateien hochladen'">
		<el-upload
			ref="upload"
			drag
			action=""
			:file-list="uploadedFiles"
			:auto-upload="false"
			:accept="'image/jpeg,image/heic,image/png,application/pdf,audio/mpeg,video/mp4,video/quicktime'"
			:multiple="true"
			:on-change="handleFileChange"
		>
			<el-icon class="el-icon--upload"><upload-filled /></el-icon>
			<div class="el-upload__text">Dateien hier ablegen oder <em>auswählen</em></div>

			<template #tip>
				<div class="el-upload__tip">Zugelassen: JPEG, HEIC, PNG, PDF, MP3, MP4 & MOV bis zu 100 MB</div>
			</template>
		</el-upload>

		<template #footer>
			<span class="info--upload">
				Hinweis: Die Dateien werden hochgeladen, sobald Sie
				<template v-if="pageType === 'beschwerde'">die Beschwerde </template>
				<template v-if="pageType === 'fall'">den Fall </template>
				<template v-if="pageType === 'rueckfrage'">die Rückfrage </template>
				<template v-if="pageType === 'stellungnahme'">die Stellungnahme </template>
				speichern.
			</span>

			<div class="button-panel">
				<TheButton type="danger" @buttonclick="saveFile">Dateien speichern</TheButton>
				<TheButton type="default" @buttonclick="openDialog = false">Abbrechen</TheButton>
			</div>
		</template>
	</el-dialog>
</template>

<script lang="ts">
import { defineComponent, PropType, ref } from 'vue';
import TheButton from '../../components/TheButton.vue';
import MediaSlider from '../../components/slider/MediaSlider.vue';
import { Plus } from '@element-plus/icons-vue';
import { texts } from '../../utils/texts';
import { DetailFile, FileDto, FileResponse } from '../../types/types';
import { UploadFilled } from '@element-plus/icons-vue';
import type { FormInstance, UploadUserFile } from 'element-plus';
import { resolveUrl } from '@/utils/url';
import heic2any from 'heic2any';

export default defineComponent({
	name: 'DetailsMedia',
	data() {
		return {
			openDialog: false,
			uploadedFiles: [] as UploadUserFile[],
			parsedFiles: [] as DetailFile[],
		};
	},
	setup() {
		const formRef = ref<FormInstance>();
		return {
			formRef,
		};
	},
	props: {
		files: {
			type: Array as PropType<DetailFile[]>,
			default: () => [],
		},
		showAddButton: {
			type: Boolean,
			default: true,
		},
		openDialogFromOutside: {
			type: Boolean,
			default: false,
		},
		pageType: {
			type: String,
			default: '',
			required: true,
		},
		allowDeleteFromServer: {
			type: Boolean,
			default: false,
		},
	},
	emits: ['addToFiles', 'removeFromFiles'],
	computed: {
		texts() {
			return texts;
		},
		allFiles() {
			const allFiles = [...this.files, ...this.uploadedFiles];
			return allFiles;
		},
	},
	components: {
		TheButton,
		Plus,
		MediaSlider,
		UploadFilled,
	},
	methods: {
		openTheDialog() {
			this.openDialog = true;
		},
		async handleFileChange(file: UploadUserFile) {
			if (file.raw && (file.raw.type === 'image/heic' || file.name.endsWith('.heic'))) {
				try {
					const convertedBlob = await heic2any({
						blob: file.raw,
						toType: 'image/png',
					});
					const convertedFile = new File([convertedBlob as BlobPart], file.name.replace(/\.heic$/, '.png'), {
						type: 'image/png',
					});

					const uploadUserFile: UploadUserFile = {
						uid: new Date().getTime(),
						name: convertedFile.name,
						percentage: 0,
						status: 'ready',
						size: convertedFile.size,
						// @ts-ignore
						raw: convertedFile,
					};
					this.uploadedFiles.push(uploadUserFile);
				} catch (error) {
					console.error('Error converting HEIC to PNG:', error);
				}
			} else {
				this.uploadedFiles.push(file);
			}
		},
		async saveFile() {
			this.uploadedFiles.forEach((file) => {
				const existingFile = this.files.map((f) => f.file).find((f) => f.fileName === file.name);
				if (!existingFile && file.raw) {
					this.$emit('addToFiles', {
						file: {
							fileName: file.name,
							contentType: file.raw.type,
							bytes: file.raw,
							url: URL.createObjectURL(file.raw),
						},
						isNew: true,
					});
				}
			});
			this.openDialog = false;
		},
		removeFromFiles(file) {
			this.$emit('removeFromFiles', file);
		},
	},
	watch: {
		files: {
			deep: true,
			handler(newFiles) {
				this.parsedFiles = newFiles.map((f) => {
					return {
						...f,
						url: resolveUrl(f.file.url),
					};
				});
			},
		},
		parsedFiles: {
			deep: true,
			handler() {
				this.uploadedFiles = this.uploadedFiles.filter((f) => {
					return this.parsedFiles.map((pf) => pf.file.fileName).includes(f.name);
				});
			},
		},
		openDialogFromOutside: {
			deep: true,
			handler(newVal) {
				this.openTheDialog();
			},
		},
	},
	mounted() {
		this.parsedFiles = this.files.map((f) => {
			return {
				...f,
				url: resolveUrl(f.file.url),
			};
		});
	},
});
</script>

<style lang="scss" scoped>
@use '../../scss/vars.scss' as *;

/* mobile styles */
@media screen and (max-width: $mobile_maxwidth) {
	section {
		.el-row {
			flex-direction: column;
			align-items: flex-start;
		}
	}
}
</style>
